import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Rectangle } from "../components/Rectangle.js";
import { SVG } from "../components/SVG.js";
/**
 * Node renderer for reaction/molecule trees: draw a node's molecule(s) and its
 * top label inside a bordered box, and report the box's measured size.
 * @param node - The tree node to render.
 * @param nodeRendererOptions - Callbacks and limits controlling molecule extraction and styling.
 * @returns The node's rendered `element` with its `width` and `height`.
 */
export function moleculeRenderer(node, nodeRendererOptions) {
    const molecules = getMolecules(node, nodeRendererOptions);
    const topLabel = getTopLabel(node, nodeRendererOptions);
    const width = Math.max(molecules.width, topLabel.width);
    const height = Math.max(molecules.height, topLabel.height);
    return {
        width,
        height,
        element: (_jsxs("g", { children: [_jsx(Rectangle, { width: width, height: height, style: {
                        stroke: 'black',
                        fill: 'white',
                        ...node.style,
                        ...nodeRendererOptions.getBoxStyle?.(node),
                    } }), molecules.element, topLabel.element] })),
    };
}
function getTopLabel(node, options = {}) {
    const label = options.getTopLabel?.(node) || node.label;
    if (!label) {
        return {
            width: 0,
            height: 0,
            element: null,
        };
    }
    return {
        width: 200,
        height: 20,
        element: (_jsx("text", { x: 0, y: -6, textAnchor: "start", stroke: "none", fontSize: "14", fill: "black", children: label })),
    };
}
/**
 * Lay out every molecule of a node side by side, scaling each to fit the size
 * limits; supports reaction nodes that carry several molecules.
 * @param node - The tree node whose molecules are drawn.
 * @param options - Molecule extraction callbacks and per-molecule size limits.
 * @returns The combined drawing with its overall `width` and `height`.
 */
function getMolecules(node, options) {
    const { maxWidth = 200, maxHeight = 150, getMolecules, getMoleculeStyle, } = options;
    if (!getMolecules) {
        throw new Error('getMolecules is not defined');
    }
    const molecules = getMolecules(node);
    if (!molecules) {
        return {
            width: 0,
            height: 0,
            element: undefined,
        };
    }
    const svgs = molecules.filter(Boolean).map((molecule, index) => {
        const svg = molecule.toSVG(maxWidth, maxHeight, undefined, {
            autoCrop: true,
            autoCropMargin: 10,
            suppressChiralText: true,
        });
        if (getMoleculeStyle) {
            return addStyleToSVG(svg, getMoleculeStyle(molecule, node, index));
        }
        return svg;
    });
    const { svg, width, height } = joinSVGs(svgs);
    return {
        width,
        height,
        element: _jsx(SVG, { svg: svg }),
    };
}
function getPlus(options = {}) {
    const { size = 11, padding = 5, strokeWidth = 2 } = options;
    const x1 = padding + size / 2;
    const y1 = padding;
    const x2 = padding + size / 2;
    const y2 = padding + size;
    return {
        svg: `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="rgb(0,0,0)" stroke-width="${strokeWidth}"></line>` +
            `<line x1="${y1}" y1="${x1}" x2="${y2}" y2="${x2}" stroke="rgb(0,0,0)" stroke-width="${strokeWidth}"></line>`,
        width: size + padding * 2,
        height: size + padding * 2,
    };
}
function joinSVGs(svgs) {
    const results = [];
    let maxHeight = 0;
    for (const svg of svgs) {
        const size = getSVGViewBox(svg);
        if (results.length > 0) {
            results.push(getPlus());
        }
        results.push({ svg, width: size.width, height: size.height });
        maxHeight = Math.max(maxHeight, size.height);
    }
    let currentWidth = 0;
    for (const result of results) {
        const shiftX = currentWidth;
        const shiftY = (maxHeight - result.height) / 2;
        result.shiftedSVG = `<g transform="translate(${shiftX},${shiftY})">${result.svg}</g>`;
        currentWidth += result.width;
    }
    return {
        width: currentWidth,
        height: maxHeight,
        svg: results.map((result) => result.shiftedSVG).join('\n'),
    };
}
function getSVGViewBox(svg) {
    const match = svg.match(/.*viewBox="(?<minX>-?\d+) (?<minY>-?\d+) (?<width>\d+) (?<height>\d+)".*/);
    if (!match?.groups) {
        throw new Error('ViewBox not found');
    }
    // convert to numbers and return the object
    return {
        minX: Number(match.groups.minX),
        minY: Number(match.groups.minY),
        width: Number(match.groups.width),
        height: Number(match.groups.height),
    };
}
function styleObjectToString(style) {
    const styleArray = [];
    for (const property in style) {
        if (Object.hasOwn(style, property)) {
            const kebabCaseProperty = property
                .replaceAll(/([A-Z])/g, '-$1')
                .toLowerCase();
            styleArray.push(`${kebabCaseProperty}: ${style[property]}`);
        }
    }
    return styleArray.join('; ');
}
function addStyleToSVG(svg, style) {
    if (!style)
        return svg;
    const size = getSVGViewBox(svg);
    const match = svg.match(/<svg.*>/);
    if (!match) {
        throw new Error('SVG tag not found');
    }
    const svgTag = match[0];
    const styleString = styleObjectToString(style);
    const styleRectange = `<rect x="${size.minX}" y="${size.minY}" width="${size.width}" height="${size.height}" style="${styleString}"></rect>`;
    return svg.replace(svgTag, `${svgTag}${styleRectange}`);
}
//# sourceMappingURL=moleculeRenderer.js.map